import Vue from "vue";
import { Dialog } from "element-ui";
import router from "@/router";
import store from "@/store";
import { PopupManager } from "element-ui/lib/utils/popup";
import CustomDialog from "./CustomDialog.vue";

/**
 * @typedef {Object} LoaderWindowOptions
 * @property {() => Promise<{ default: typeof Vue }>} component Async factory that resolves to the Vue component shown inside the dialog.
 * @property {Object<string, any>} [props] Props forwarded to the loaded component.
 * @property {string} [title] Dialog title shown in the custom header.
 * @property {string|number} [width] Width passed to the underlying Element dialog.
 * @property {string|number} [height] Optional fixed height applied to the dialog body.
 * @property {boolean} [showHeader] Whether to render the Element dialog header; defaults to false because a custom header is injected.
 * @property {boolean} [padding] Toggle for the dialog body padding.
 * @property {boolean} [closeOnClickModal] Mirrors the Element dialog prop with the same name.
 * @property {boolean} [closeOnPressEscape] Mirrors the Element dialog prop with the same name.
 * Additional Element-UI dialog props can be provided and will be forwarded automatically.
 */

function getPublicData(data) {
  const result = {};
  Object.keys(data).forEach((key) => {
    if (!key.startsWith("_") && !key.startsWith("$")) {
      result[key] = data[key];
    }
  });
  return result;
}

const LoaderDialog = Vue.extend({
  components: { ElDialog: Dialog },
  data() {
    return {
      visible: false,
      innerProps: {},
      Comp: null,
      height: undefined, // 新增 heigh
    };
  },
  mounted() {
    // 弹窗挂载后再显示，确保 open 事件能被订阅
    this.$nextTick(() => {
      this.visible = true;
    });
  },
  methods: {
    close(result) {
      const me = this;
      me.visible = false;
      me.$nextTick(() => {
        me.$destroy();
        me.$el.parentNode && me.$el.parentNode.removeChild(me.$el);
        me.resolve(result);
      });
    },
    onDialogOpen() {
      const me = this;
      // 手动提升全局 popup zIndex
      if (PopupManager && typeof PopupManager.nextZIndex === "function") {
        PopupManager.nextZIndex();
      }
      // 设置 el-dialog 高度
      if (me.height) {
        me.$nextTick(async () => {
          const dialog = me.$el.querySelector(".el-dialog");
          if (dialog) {
            if (me.height) dialog.style.height = me.height;
            dialog.style.display = "flex";
            dialog.style.flexDirection = "column";
            const dialogBody = dialog.querySelector(".el-dialog__body");
            if (dialogBody) {
              dialogBody.style.flex = "1";
              dialogBody.style.display = "flex";
              dialogBody.style.flexDirection = "column";
            }
            //派发resize
            window.dispatchEvent(new Event("resize"));
          }
        });
      }
    },
    setFullScreen() {
      console.log("Setting full screen");
      const dialog = this.$el.querySelector(".el-dialog");
      if (dialog) {
        dialog.classList.add("full-screen");
        this.$forceUpdate();
      }
    },
    exitFullScreen() {
      console.log("Exiting full screen");
      const dialog = this.$el.querySelector(".el-dialog");
      if (dialog) {
        dialog.classList.remove("full-screen");
        this.$forceUpdate();
      }
    },
  },
  render(h) {
    const me = this;
    if (!me.Comp) return null;
    // 最大化/还原按钮
    const isFullScreen = () => {
      const dialog = me.$el && me.$el.querySelector(".el-dialog");
      return dialog && dialog.classList.contains("full-screen");
    };
    const maximizeBtn = h(
      "el-button",
      {
        props: {
          type: "text",
        },
        style: { marginRight: "8px" },
        on: {
          click: () => {
            if (isFullScreen()) {
              me.exitFullScreen();
            } else {
              me.setFullScreen();
            }
          },
        },
      },
      [
        h("i", {
          class: isFullScreen()
            ? "fa fa-window-restore"
            : "fa fa-window-maximize",
          style: { fontSize: "18px" },
        }),
      ]
    );
    // 关闭按钮
    const closeBtn = h(
      "el-button",
      {
        props: {
          icon: "el-icon-close",
          type: "text",
        },
        on: {
          click: () => {
            me.close();
            if (typeof me.reject === "function") {
              me.reject("cancel");
              me.reject = null;
            }
          },
        },
      },
      []
    );
    // 标题
    // 将 title 传递给内部组件
    const titleText =
      (me.$data && me.$data.title) || (me.$props && me.$props.title) || "";
    if (me.innerProps) {
      me.innerProps.title = titleText;
    }
    // header 区域（如需自定义可在内部组件实现）
    const titleNode = h(
      "span",
      { style: { fontWeight: "bold", fontSize: "16px" } },
      [titleText]
    );
    const header = h(
      "div",
      {
        style: {
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0 20px",
          minHeight: "48px",
        },
      },
      [
        titleNode,
        h("div", { style: { display: "flex", alignItems: "center" } }, [
          maximizeBtn,
          closeBtn,
        ]),
      ]
    );
    return h(
      CustomDialog,
      {
        props: {
          visible: me.visible,
          ...me.$data,
          ...me.$props,
          // 禁用默认 header
          showClose: false,
        },
        on: {
          "update:visible": (v) => {
            me.visible = v;
            if (typeof me.reject === "function") {
              me.reject("cancel");
              me.reject = null;
            }
            if (!v) me.close();
          },
          open: me.onDialogOpen,
          max: () => me.setFullScreen(),
          min: () => me.exitFullScreen(),
        },
        scopedSlots: {
          title: () => header,
        },
      },
      [
        h(me.Comp, {
          props: me.innerProps,
          on: {
            confirm: (val) => {
              me.close();
              me.resolve(val);
            },
            cancel: () => {
              me.close();
              me.reject("cancel");
            },
            close: me.close,
            max: () => me.setFullScreen(),
            min: () => me.exitFullScreen(),
            toggle: () => {
              if (isFullScreen()) {
                me.exitFullScreen();
              } else {
                me.setFullScreen();
              }
            },
          },
        }),
      ]
    );
  },
});

/**
 * 弹出一个 Element-UI Dialog 并动态加载 Vue 组件，返回一个 Promise，
 * 根据子组件触发的 confirm/cancel 事件 resolve 或 reject。
 *
 * @param {LoaderWindowOptions} options Dialog 配置与加载参数。
 * @returns {Promise<unknown>} 当内部组件调用 confirm/cancel/close 时结束。
 */
async function openWindow({ component, props = {}, ...dialogProps }) {
  let rejectFn;
  let resolveFn;

  const loaderPromise = new Promise((resolve, reject) => {
    resolveFn = resolve;
    rejectFn = reject;
  });
  const Comp = (await component()).default;
  const instance = new LoaderDialog({
    data: {
      Comp,
      innerProps: props,
      height: props.height, // 传递 height
      ...getPublicData(dialogProps), // 直接展开到 data
    },
    router,
    store,
  });
  instance.resolve = resolveFn;
  instance.reject = rejectFn;
  instance.$mount();
  document.body.appendChild(instance.$el);
  return loaderPromise;
}

// LoaderWindow 静态类封装
export class LoaderWindow {
  /**
   * 打开一个新的 LoaderWindow 对话框。
   *
   * @param {LoaderWindowOptions} options 同 openWindow 的参数。
   * @returns {Promise<unknown>} 解析为内部组件通过 confirm 传出的值。
   */
  static open(options) {
    return openWindow(options);
  }
}

// Vue2 插件包装
export const LoaderWindowPlugin = {
  install(Vue) {
    /**
     * 打开一个带有自定义 Vue 组件的异步对话框。
     *
     * @type {(options: LoaderWindowOptions) => Promise<unknown>}
     */
    Vue.prototype.$loaderWindow = LoaderWindow.open;
  },
};
